import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AuthState } from '../types';
import apiService from '../services/api';
import socketService from '../services/socket';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginSimple: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (updatedUser: User) => void;
  validateToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: { user: User; token: string } }
  | { type: 'CLEAR_USER' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'CLEAR_USER':
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [sessionTimeout, setSessionTimeout] = React.useState<number>(60); // Default 60 minutes
  const sessionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = React.useRef<Date>(new Date());
  const isRefreshingRef = React.useRef<boolean>(false);

  // Load system settings on mount
  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/system/settings`);
        if (response.ok) {
          const data = await response.json();
          setSessionTimeout(data.settings?.sessionTimeout || 60);
        }
      } catch (error) {
        console.error('Error loading system settings for session timeout:', error);
      }
    };
    loadSystemSettings();
  }, []);

  const logout = (): void => {
    // Clear timeout first
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    
    // Disconnect socket
    socketService.disconnect();
    // Clear local storage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    // Clear state
    dispatch({ type: 'CLEAR_USER' });
  };

  // Reset session timeout on activity
  const resetSessionTimeout = React.useCallback(() => {
    if (!state.isAuthenticated) return;
    
    lastActivityRef.current = new Date();
    
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    
    sessionTimeoutRef.current = setTimeout(() => {
      console.log('Session timeout reached - logging out user');
      logout();
      alert('Your session has expired due to inactivity. Please log in again.');
    }, sessionTimeout * 60 * 1000); // Convert minutes to milliseconds
  }, [state.isAuthenticated, sessionTimeout]);

  // Set up activity listeners
  useEffect(() => {
    if (state.isAuthenticated) {
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      const handleActivity = () => {
        resetSessionTimeout();
      };
      
      // Add event listeners
      activityEvents.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });
      
      // Start initial timeout
      resetSessionTimeout();
      
      return () => {
        // Remove event listeners
        activityEvents.forEach(event => {
          document.removeEventListener(event, handleActivity, true);
        });
        
        // Clear timeout
        if (sessionTimeoutRef.current) {
          clearTimeout(sessionTimeoutRef.current);
        }
      };
    }
  }, [state.isAuthenticated, resetSessionTimeout]);

  // Detect page refresh to prevent unnecessary logouts
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      console.log('AuthContext: Page refresh/close detected, setting refresh flag');
      isRefreshingRef.current = true;
      // Store refresh flag in sessionStorage (survives page refresh but not tab close)
      // Also store a timestamp to detect stale flags
      sessionStorage.setItem('page_refreshing', 'true');
      sessionStorage.setItem('page_refresh_time', Date.now().toString());
      
      // Store auth state to restore after refresh
      if (state.token && state.user) {
        sessionStorage.setItem('auth_refresh_backup', JSON.stringify({
          token: state.token,
          user: state.user,
          timestamp: Date.now()
        }));
      }
    };

    const handleLoad = () => {
      // Check if this was a page refresh
      const wasRefreshing = sessionStorage.getItem('page_refreshing');
      const refreshTime = sessionStorage.getItem('page_refresh_time');
      
      if (wasRefreshing) {
        console.log('AuthContext: Page refresh completed, clearing refresh flag');
        
        // Check if refresh happened recently (within last 10 seconds)
        const timeSinceRefresh = refreshTime ? Date.now() - parseInt(refreshTime) : 0;
        if (timeSinceRefresh < 10000) {
          console.log('AuthContext: Recent refresh detected, keeping user logged in');
          isRefreshingRef.current = true;
          
          // Keep the flag for a short time to allow token validation to complete
          setTimeout(() => {
            isRefreshingRef.current = false;
            sessionStorage.removeItem('page_refreshing');
            sessionStorage.removeItem('page_refresh_time');
            console.log('AuthContext: Refresh grace period ended');
          }, 3000); // 3 second grace period
        } else {
          // Stale flag, clear it
          console.log('AuthContext: Stale refresh flag detected, clearing');
          isRefreshingRef.current = false;
          sessionStorage.removeItem('page_refreshing');
          sessionStorage.removeItem('page_refresh_time');
        }
      }
    };

    // Check on mount if we just refreshed
    handleLoad();

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('load', handleLoad);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('load', handleLoad);
    };
  }, [state.token, state.user]);

  useEffect(() => {
    // Check for existing auth token on app load
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        dispatch({ type: 'SET_USER', payload: { user, token } });
        // Connect socket with existing token
        socketService.connect(token);
        
        // Validate the token to make sure it's still valid (with retry logic)
        console.log('AuthContext: Auto-validating existing token on app load...');
        
        const validateWithRetry = async (attempts = 5) => { // Increased attempts for better reliability
          for (let i = 0; i < attempts; i++) {
            try {
              console.log(`AuthContext: Validation attempt ${i + 1}/${attempts}`);
              
              // Check if we're in a page refresh scenario
              const isPageRefreshing = sessionStorage.getItem('page_refreshing') === 'true';
              const refreshTime = sessionStorage.getItem('page_refresh_time');
              const timeSinceRefresh = refreshTime ? Date.now() - parseInt(refreshTime) : Infinity;
              const isRecentRefresh = timeSinceRefresh < 10000; // Within 10 seconds
              
              if (isPageRefreshing && isRecentRefresh && i === 0) {
                console.log('AuthContext: Page refresh detected, using backup auth state if validation fails');
              }
              
              const { user: validatedUser, tokenValid } = await apiService.validateToken();
              
              if (tokenValid) {
                console.log('AuthContext: Auto-validation successful');
                // Update with latest user data
                dispatch({ type: 'SET_USER', payload: { user: validatedUser, token } });
                localStorage.setItem('user_data', JSON.stringify(validatedUser));
                
                // Clear refresh backup after successful validation
                sessionStorage.removeItem('auth_refresh_backup');
                return; // Success, exit retry loop
              } else {
                console.log('AuthContext: Token is invalid, clearing session');
                logout();
                return; // Token is invalid, don't retry
              }
            } catch (error) {
              console.error(`AuthContext: Validation attempt ${i + 1} failed:`, error);
              
              if (i === attempts - 1) {
                // Last attempt failed - check if it's a network error or page refresh
                const isNetworkError = error instanceof Error && (
                  error.message.includes('fetch') ||
                  error.message.includes('network') ||
                  error.message.includes('Failed to fetch') ||
                  error.message.includes('NetworkError') ||
                  error.message.includes('timeout')
                );
                
                const wasRefreshing = sessionStorage.getItem('page_refreshing') === 'true';
                const isRefreshing = isRefreshingRef.current;
                
                if (isNetworkError || wasRefreshing || isRefreshing) {
                  console.log('AuthContext: Network error or page refresh during validation, keeping user logged in');
                  console.log('AuthContext: Using cached credentials from localStorage');
                  
                  // Try to restore from backup if available
                  const authBackup = sessionStorage.getItem('auth_refresh_backup');
                  if (authBackup) {
                    try {
                      const backup = JSON.parse(authBackup);
                      const backupAge = Date.now() - backup.timestamp;
                      if (backupAge < 30000) { // Backup is less than 30 seconds old
                        console.log('AuthContext: Restoring from auth backup');
                        dispatch({ type: 'SET_USER', payload: { user: backup.user, token: backup.token } });
                        localStorage.setItem('user_data', JSON.stringify(backup.user));
                        localStorage.setItem('auth_token', backup.token);
                      }
                    } catch (e) {
                      console.error('AuthContext: Error restoring auth backup:', e);
                    }
                  }
                  
                  // Keep user logged in with existing data
                  return;
                } else {
                  console.log('AuthContext: Non-network error and not refreshing, clearing session');
                  logout();
                }
              } else {
                // Wait before retrying (shorter delays for faster recovery on F5)
                const delay = i === 0 ? 500 : Math.min(Math.pow(2, i) * 500, 3000);
                console.log(`AuthContext: Retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            }
          }
        };
        
        validateWithRetry();
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
      }
    }

    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    console.log('AuthContext: Starting login for', email);
    
    try {
      const { user, token } = await apiService.login(email, password);
      console.log('AuthContext: Login successful, received user:', user);
      console.log('AuthContext: User role:', user.role, 'userType:', user.userType);
      
      dispatch({ type: 'SET_USER', payload: { user, token } });
      console.log('AuthContext: User state updated');
      
      // Connect socket after successful login
      socketService.connect(token);
      console.log('AuthContext: Socket connected');
    } catch (error) {
      console.error('AuthContext: Login failed:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const loginSimple = async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    console.log('AuthContext: Starting simplified login for', email);
    
    try {
      const { user, token } = await apiService.loginSimple(email, password);
      console.log('AuthContext: Simplified login successful, received user:', user);
      
      dispatch({ type: 'SET_USER', payload: { user, token } });
      console.log('AuthContext: User state updated');
      
      // Connect socket after successful login
      socketService.connect(token);
      console.log('AuthContext: Socket connected');
    } catch (error) {
      console.error('AuthContext: Simplified login failed:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const validateToken = async (): Promise<boolean> => {
    console.log('AuthContext: Validating current token...');
    
    if (!state.token) {
      console.log('AuthContext: No token to validate');
      return false;
    }
    
    try {
      const { user, tokenValid } = await apiService.validateToken();
      
      if (tokenValid) {
        console.log('AuthContext: Token validation successful');
        // Update user data in case permissions have changed
        dispatch({ type: 'SET_USER', payload: { user, token: state.token } });
        localStorage.setItem('user_data', JSON.stringify(user));
        return true;
      } else {
        console.log('AuthContext: Token validation failed');
        logout();
        return false;
      }
    } catch (error) {
      console.error('AuthContext: Token validation error:', error);
      logout();
      return false;
    }
  };

  const register = async (name: string, email: string, password: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const { user, token } = await apiService.register(name, email, password);
      dispatch({ type: 'SET_USER', payload: { user, token } });
      // Connect socket after successful registration
      socketService.connect(token);
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const updateUser = (updatedUser: User): void => {
    if (state.token) {
      // Update local storage
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
      // Update state
      dispatch({ type: 'SET_USER', payload: { user: updatedUser, token: state.token } });
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginSimple,
    register,
    logout,
    updateUser,
    validateToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 